import React, { createContext, useState, useContext, useEffect } from 'react';
import { subscribeToAuthChanges, getUserDocument, logout as firebaseLogout } from '../firebase/auth';
import { apiClient } from '../utils/apiClient';
import { impersonationState } from '../services/impersonationState';
import EventBusService from '../services/EventBusService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Support impersonation state
  const [impersonation, setImpersonation] = useState({
    isImpersonating: false,
    sessionId: null,
    adminId: null,
    adminEmail: null,
    targetUserId: null,
    targetUserEmail: null,
    targetUserName: null,
    startedAt: null,
    expiresAt: null
  });

  // Sync impersonation state with apiClient and axios interceptors whenever it changes
  useEffect(() => {
    apiClient.setImpersonation(impersonation);
    impersonationState.set(impersonation);
  }, [impersonation]);

  // Auto-logout when impersonation session expires
  useEffect(() => {
    if (!impersonation.isImpersonating || !impersonation.expiresAt) {
      return;
    }

    const expiryTime = new Date(impersonation.expiresAt).getTime();
    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;

    if (timeUntilExpiry <= 0) {
      // Already expired - exit immediately
      exitImpersonation();
      return;
    }

    // Set timeout to auto-exit when session expires
    const timeoutId = setTimeout(async () => {
      console.log('🕒 Impersonation session expired - auto-exiting...');
      try {
        await exitImpersonation();
        // Toast notification would be nice here
        alert('Impersonation session has expired. Returning to admin view.');
      } catch (error) {
        console.error('Error auto-exiting impersonation:', error);
      }
    }, timeUntilExpiry);

    return () => clearTimeout(timeoutId);
  }, [impersonation.isImpersonating, impersonation.expiresAt]);

  useEffect(() => {
    // Subscribe to auth state changes (handles both popup and redirect OAuth flows)
    const unsubscribe = subscribeToAuthChanges(async (user) => {
      setCurrentUser(user);

      if (user) {
        // Fetch user profile from Firestore
        // Use the user parameter from Firebase callback, not the stale currentUser state
        const profile = await getUserDocument(user.uid);
        setUserProfile(profile);

        // Sync event bus inbox (silent, fire-and-forget)
        // Only sync if user has an active subscription — avoids 403 console noise
        if (profile?.subscriptionStatus === 'active' || profile?.subscription?.status === 'active') {
          EventBusService.syncInbox().catch(() => {});
        }
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const refreshUserProfile = async () => {
    if (currentUser) {
      const profile = await getUserDocument(currentUser.uid);
      setUserProfile(profile);
    }
  };

  const updateUserProfile = async (updates) => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    try {
      const API_URL = process.env.REACT_APP_SECURE_API_URL ||
        'https://us-central1-booker-1d469.cloudfunctions.net/secureApi';

      const token = await currentUser.getIdToken();

      const response = await fetch(`${API_URL}/user/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update profile');
      }

      // Refresh user profile to get updated data
      await refreshUserProfile();

      return { success: true };
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  // Enhanced logout with storage clearing
  const logout = async () => {
    try {
      const result = await firebaseLogout();
      // Clear local state immediately for security
      setCurrentUser(null);
      setUserProfile(null);
      setError(null);
      // Clear impersonation state on logout
      setImpersonation({
        isImpersonating: false,
        sessionId: null,
        adminId: null,
        adminEmail: null,
        targetUserId: null,
        targetUserEmail: null,
        targetUserName: null,
        startedAt: null,
        expiresAt: null
      });
      return result;
    } catch (error) {
      console.error('Logout error:', error);
      // Clear local state even if logout fails
      setCurrentUser(null);
      setUserProfile(null);
      return { error: error.message };
    }
  };

  // Start support impersonation
  const startImpersonation = async (userId) => {
    if (!currentUser) {
      throw new Error('Support not authenticated');
    }

    try {
      const API_URL = process.env.REACT_APP_SECURE_API_URL ||
        'https://us-central1-booker-1d469.cloudfunctions.net/secureApi';

      const token = await currentUser.getIdToken();

      const response = await fetch(`${API_URL}/support/impersonate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to start impersonation');
      }

      const data = await response.json();

      // Set impersonation state
      setImpersonation({
        isImpersonating: true,
        ...data.impersonation
      });

      // Fetch and set the target user's profile
      const targetProfile = await getUserDocument(userId);
      setUserProfile(targetProfile);

      return { success: true, impersonation: data.impersonation };
    } catch (error) {
      console.error('Error starting impersonation:', error);
      throw error;
    }
  };

  // Exit support impersonation
  const exitImpersonation = async () => {
    if (!impersonation.isImpersonating) {
      return { success: true };
    }

    try {
      const API_URL = process.env.REACT_APP_SECURE_API_URL ||
        'https://us-central1-booker-1d469.cloudfunctions.net/secureApi';

      const token = await currentUser.getIdToken();

      const response = await fetch(`${API_URL}/support/exit-impersonation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: impersonation.sessionId,
          targetUserId: impersonation.targetUserId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to exit impersonation');
      }

      // Clear impersonation state
      setImpersonation({
        isImpersonating: false,
        sessionId: null,
        adminId: null,
        adminEmail: null,
        targetUserId: null,
        targetUserEmail: null,
        targetUserName: null,
        startedAt: null,
        expiresAt: null
      });

      // Restore admin's profile
      await refreshUserProfile();

      return { success: true };
    } catch (error) {
      console.error('Error exiting impersonation:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    error,
    setError,
    refreshUserProfile,
    updateUserProfile,
    logout,
    isAuthenticated: !!currentUser,
    isAdmin: userProfile?.role === 'admin' || userProfile?.isAdmin === true,
    isProvider: userProfile?.role === 'provider',
    // Impersonation
    impersonation,
    startImpersonation,
    exitImpersonation
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
